<template>
	<div id="app">
		{{ stateSync }}
		<div
			:class="{ 'app-loading-blur': !isAppReady, 'app-content-wrapper': true }"
		>
			<LoginModal />
			<router-view />
		</div>
		<SignupModal :appBootstrapped="appBootstrapped && hasAccess()" />
		<Internet />
		<InsightsHud v-if="hudAllowed" />
		<div v-if="showGlobalSpinner" class="global-route-loader">
			<Spinner />
		</div>
	</div>
</template>
<script>
import {
	userInfo,
	getCandidatePrimaryProfile,
	checkCandidateSignin,
	getCandidateOnboard,
	getDeviceActivity,
	getHudInsights,
} from "./utils/apiFunctions.js";
import {
	startTokenExpiryWatcher,
	checkAndSetAccess,
	hasAccess,
	notGoogleLogin,
} from "./utils/tokenCheck.js";
import { getProcessedToken } from "./utils/request.js";
import EventBus from "./utils/EventBus.js";
import Internet from "./components/fixedcomponents/Internet.vue";
import LoginModal from "@/components/common/LoginModal.vue";
import notifications from "./utils/notifications";
import SignupModal from "@/components/Onboarding/SignupModal.vue";
import GlobalOnboardingFlow from "@/components/common/GlobalOnboardingFlow.vue";
import Spinner from "@/components/fixedcomponents/Spinner.vue";
import { mapGetters } from "vuex";
export default {
	components: {
		Internet,
		LoginModal,
		SignupModal,
		GlobalOnboardingFlow,
		Spinner,
		// Loaded on its own, and only for allowed testers, so everyone else never downloads it.
		InsightsHud: () => import("@/components/InsightsHud/InsightsHud.vue"),
	},
	mixins: [notifications],
	name: "App",
	data() {
		return {
			isBooting: true,
			hudAllowed: false,
			// userID the panel check last ran for, so it runs again after a login or logout.
			hudCheckedFor: undefined,
			isAppReady: false,
			excludedOnboardingPages: [
				"Login",
				"PageNotFound",
				"Forgot",
				"IdCapture",
				"ViewPublicProfile",
				"CvsSubmitted",
				"InterviewDetails",
			],
		};
	},
	// Save data on reload
	async created() {
		var savedstate = localStorage.getItem("simpAdminState");
		if (savedstate != null) {
			this.$store.replaceState(JSON.parse(savedstate));
		}
		checkAndSetAccess();
		if (this.hasAccess()) startTokenExpiryWatcher();
		if (notGoogleLogin()) this.listenForSignout();

		try {
			if (this.hasAccess()) await this.getuserInfo();
		} finally {
			this.checkHudAccess();
			let done = false;
			const stopBooting = () => {
				if (done) return;
				done = true;
				this.$nextTick(() => {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							this.isBooting = false;
						});
					});
				});
			};

			// Both success and error callbacks to handle redirects/errors on cold starts
			this.$router.onReady(stopBooting, (err) => {
				stopBooting();
			});
		}
		EventBus.$on("set-is-bootstrapped-false", () => {
			this.isBooting = false;
		});
	},
	async mounted() {
		if (this.hasAccess()) await this.checkAuthAndOnboardingFallback();
	},
	destroyed() {
		EventBus.$off("set-is-bootstrapped-false");
	},

	watch: {
		$route(to, from) {
			this.$root.$emit("route-changed", { to, from });
			this.checkHudAccess();
			const loginRouteNames = ["OTPVerification", "Register", "Forgot", "reset", "Login"];
			if(loginRouteNames.includes(to.name)) {
				return;
			}
			this.getuserInfo();
		},
	},
	computed: {
		...mapGetters("userVUEX", {
			userInfo: "getUserInfo",
		}),
		showGlobalSpinner() {
			return this.isBooting;
		},
		appBootstrapped() {
			return !this.excludedOnboardingPages.includes(this.$route.name);
		},
		stateSync() {
			var stt = this.$store.state;
			localStorage.setItem("simpAdminState", JSON.stringify(stt));
			return "";
		},
	},
	methods: {
		hasAccess() {
			return hasAccess();
		},
		// Testers' insights panel: the backend allows it per userID (HUD_USER_IDS) and answers 404 for everyone else.
		async checkHudAccess() {
			const userID = this.hasAccess() ? getProcessedToken() : null;
			if (userID === this.hudCheckedFor) return;
			this.hudCheckedFor = userID;
			if (!userID) {
				this.hudAllowed = false;
				return;
			}
			try {
				const res = await getHudInsights();
				// Busy or briefly down still means allowed; only 404 (not listed) and 401 hide it.
				if (userID === this.hudCheckedFor) this.hudAllowed = [200, 429, 503].includes(res && res.status);
			} catch (err) {
				if (userID === this.hudCheckedFor) this.hudAllowed = false;
			}
		},
		async checkAuthAndOnboardingFallback() {
			await this.$nextTick();
			const userID = getProcessedToken();
			if (!userID) return;

			// Wait for router's beforeEach to finish (it sets signinCheckDone).
			await new Promise((resolve) => setTimeout(resolve, 300));
			// if (this.$store.getters["candidateVUEX/getSigninCheckDone"]) return;

			console.log("[Onboarding][App] router check not done — running fallback");
			try {
				const onboardRes = await getCandidateOnboard();
				const onboardData = onboardRes?.data?.data;
				if (
					onboardData?.onboardingCompleted === true ||
					onboardData?.onboardingCompleted === null
				) {
					return; // onboarding already done, nothing to show
				}
				const checkRes = await checkCandidateSignin({
					check_completion: "true",
					userID,
				});
				if (checkRes?.data?.type === "Success") {
					const signinCheckData = checkRes.data.data;
					const isCandVal =
						signinCheckData?.isCandidate ??
						signinCheckData?.iscandidate ??
						signinCheckData?.is_candidate;
					if (isCandVal === false || isCandVal === "false") {
						EventBus.$emit("show-signup-form", { signinCheckData });
					} else if (onboardData && onboardData.onboardingCompleted === false) {
						const step = onboardData.onboardingStep;
						if (step === "skills" || step === "goals") {
							EventBus.$emit("show-onboarding-modal", { step });
						} else if (step === "resumeUpload") {
							sessionStorage.setItem("fromOnboarding", "true");
							EventBus.$emit("show-onboarding-modal", {
								step: "resumeUpload",
							});
						} else {
							EventBus.$emit("show-onboarding-modal");
						}
					}
				}
			} catch (err) {
				console.error("[Onboarding][App] fallback check error:", err);
			} finally {
				this.$store.commit("candidateVUEX/setSigninCheckDone", true);
			}
		},
		async getuserInfo() {
			try {
				const res = await userInfo();
				if (res.status == 200) {
					this.$store.commit("userVUEX/setUserInfo", res.data.data);
					if (notGoogleLogin()){
						this.LoginTosocket();
					}
				}
			} catch (error) {
				console.error(error);
			}
			
		},
		async listenForSignout() {
			this.$socket.on("signedOut", (data) => {
				// alert(data.message); // Log or handle the signed-out message as needed
				this.$store.commit("candidateVUEX/setCandidateInfo", {});
				localStorage.setItem("refresh", true);
				// localStorage.clear();
				this.$store.dispatch("userVUEX/LogOut");
			});
		},
		async LoginTosocket() {
			try {
				const response = await getDeviceActivity();
				const objectWithCurrentKey = response.data[0].sessions.find((obj) =>
					obj.hasOwnProperty("current")
				);
				const userid = objectWithCurrentKey.id;
				localStorage.setItem("currentLogin", userid);
				const email = this.userInfo.email;
				localStorage.setItem("currentEmail", email);
				const device = response.data;
				device.forEach((deviceItem, index) => {
					const sessions = deviceItem.sessions;

					const currentIndex = sessions.findIndex(
						(item) => item.current === true
					);

					if (currentIndex !== -1) {
						const objectWithCurrentKey = sessions[currentIndex];

						const userid = objectWithCurrentKey.id;
						const email = this.userInfo.email;
						console.log("signed in");
						this.$socket.emit("signIn", {
							email: email,
							userid: userid,
						});
					}
				});
			} catch (error) {
				console.log(error)
			}
			
		},
	},
};
</script>

<style type="text/scss">
::placeholder {
	font-weight: 400;
	font-size: 12px;
	line-height: 21px;
	color: #adaaaa;
}
body {
	font-family: "Poppins", sans-serif;
}
#nav {
	padding: 30px;

	a {
		font-weight: bold;
		color: #2c3e50;

		&.router-link-exact-active {
			color: #42b983;
		}
	}
}
.global-route-loader {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	backdrop-filter: blur(2px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 99999;
}
.global-route-loader .loader {
	margin-left: -32px !important;
}
</style>
